import { SimpleCard } from "@/components/DataCard";

const bestPractices = [
  "Add random delays (15-60 seconds) between each email send.",
  "Vary your daily send times rather than using fixed schedules.",
  "Avoid sending at exactly :00 or :30 seconds.",
  "Warm up new domains with gradual volume increases over weeks.",
  "Maintain consistent daily sending volumes without spikes.",
  "Use slight variations in subject lines and content."
];

const technicalTips = [
  {
    title: "Implement jitter in your code",
    code: "delay = baseDelay + random(15, 60) // seconds"
  },
  {
    title: "Randomize send timestamps", 
    code: "sendTime = scheduledTime + random(-5, +5) // minutes"
  },
  {
    title: "Throttle batch sends",
    code: "maxPerHour = dailyVolume / 8 // spread across work hours"
  }
];

const sources = [
  {
    text: "Validity – 2024/2025 Email Deliverability Benchmark Report with ISP-specific inbox placement rates:",
    links: [
      { label: "2025 Deliverability Benchmark", url: "https://www.validity.com/resource-center/2025-email-deliverability-benchmark-report/" },
      { label: "Sender Reputation Guide", url: "https://www.validity.com/e-mail-marketing/sender-reputation/" }
    ]
  },
  {
    text: "GetResponse – Email marketing benchmarks based on billions of emails, including bot detection data:",
    links: [
      { label: "Email Marketing Benchmarks", url: "https://www.getresponse.com/resources/reports/email-marketing-benchmarks" },
      { label: "Key Insights Report", url: "https://www.getresponse.com/blog/email-marketing-benchmarks-report-key-insights" }
    ]
  },
  {
    text: "Litmus – State of Email 2025 report on AI filtering and privacy changes:",
    links: [
      { label: "State of Email Report", url: "https://www.litmus.com/resources/state-of-email" }
    ]
  },
  {
    text: "Google Postmaster Tools – Official sender guidelines and bulk sender requirements:",
    links: [
      { label: "Gmail Sender Guidelines", url: "https://support.google.com/a/answer/81126" }
    ]
  },
  {
    text: "M3AAWG (Messaging Anti-Abuse Working Group) – Industry best practices for email senders:",
    links: [
      { label: "Sender Best Practices", url: "https://www.m3aawg.org/documents-for-senders-and-esps" }
    ]
  }
];

export const SendingTimeBestPractices = () => {
  return (
    <section className="section-card mt-5 sm:mt-7">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1.5 sm:gap-2 mb-5 sm:mb-6">
        <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight text-foreground">
          How to implement human-like sending
        </h2>
        <p className="text-[11px] sm:text-xs text-muted-foreground">
          Practical techniques to avoid spam filter detection
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
        <SimpleCard title="Best practices checklist">
          <ul className="list-none space-y-2">
            {bestPractices.map((practice, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                <span className="w-1 h-1 rounded-full bg-success mt-1.5 shrink-0" />
                {practice}
              </li>
            ))}
          </ul>
        </SimpleCard>

        <SimpleCard title="Technical implementation">
          <div className="space-y-3">
            {technicalTips.map((tip, i) => (
              <div key={i} className="bg-muted/50 rounded-lg p-3">
                <div className="text-xs font-medium text-foreground mb-1">{tip.title}</div>
                <code className="text-[10px] text-primary font-mono">{tip.code}</code>
              </div>
            ))}
          </div>
        </SimpleCard>

        <SimpleCard title="Why this matters for your campaigns" className="md:col-span-2">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Email service providers like Gmail, Outlook, and Yahoo use sophisticated machine learning 
            to analyze sender behavior. Even if your content is legitimate and your domain has good 
            reputation, <strong className="text-foreground">automated-looking patterns can still hurt your inbox placement</strong>. 
            By implementing natural timing variation, you signal that you're a human sender, 
            which improves deliverability across all major providers.
          </p>
        </SimpleCard>
      </div>

      {/* Sources */}
      <div className="mt-6 sm:mt-8 pt-5 border-t border-border">
        <h3 className="font-display text-sm font-semibold text-foreground mb-3">
          Sources and further reading
        </h3>
        <div className="space-y-1.5 text-[10px] sm:text-[11px] text-muted-foreground">
          {sources.map((source, i) => (
            <p key={i} className="leading-relaxed">
              • {source.text}{" "}
              {source.links.map((link, j) => (
                <span key={j}>
                  <a 
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline transition-colors"
                  >
                    {link.label}
                  </a>
                  {j < source.links.length - 1 && " and "}
                </span>
              ))}
              .
            </p>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
          These resources provide deeper technical background on how spam filters analyze sending patterns 
          and what constitutes good sender behavior.
        </p>
      </div>
    </section>
  );
};
